import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { StatusBadge } from '@/ui/Status/Status'
import { getResourceById, promoteResource } from '../api/resources'
import { reviewFlag } from '../components/console/resourceFacets'
import { DeliveryTracePanel } from '../components/ingress/DeliveryTracePanel'
import { IngressEndpointCard } from '../components/ingress/IngressEndpointCard'
import { WorkflowsCard } from '../components/ingress/WorkflowsCard'
import { RecipeUsageCard } from '../components/recipes/RecipeUsageCard'
import {
  getResourceView,
  type ViewResource,
} from '../components/resource-views/resourceViews'
import { type Crumb, WsPageShell } from '../components/WsPageShell'
import { kindDisplay } from '../constants/resourceKinds'
import { ROUTES } from '../constants/routes'
import { resourcesCopy } from '../copy/resources'
import { useNow } from '../hooks/useNow'
import { useResourceKinds } from '../hooks/useResourceKinds'
import { useToastStore } from '../store/toast'
import { useWorkspaceStore } from '../store/workspace'
import type { TopicResource } from '../types/api'
import { resourceEditUrl } from '../utils/resourceLinks'
import { formatRelative } from '../utils/time'

/**
 * The unified resource detail page (redesign §5) — every resource
 * reference anywhere deep-links here. Scope-move actions ride the Phase-0
 * promote endpoint; the body renders through the per-kind read-side view
 * registry, under the title with its provenance line.
 */
export function ResourceDetailPage() {
  const { resourceId = '' } = useParams()
  const qc = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)
  const now = useNow()
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)

  const {
    data: resource,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['resource', 'detail', resourceId],
    queryFn: () => getResourceById(resourceId),
    retry: false,
    staleTime: 5_000,
  })

  const showSkeleton = useSkeletonDelay(isLoading)
  const { data: kinds = [] } = useResourceKinds()

  const workspace = workspaces.find(
    (w) =>
      w.workspace_id === (resource?.workspace_id ?? activeWorkspaceId ?? ''),
  )

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['resource'] })
    void qc.invalidateQueries({ queryKey: ['resources'] })
    void qc.invalidateQueries({ queryKey: ['thread-resources'] })
    void qc.invalidateQueries({ queryKey: ['waiting'] })
  }

  const promote = useMutation({
    mutationFn: (to: 'topic' | 'workspace') => {
      if (!resource?.topic_id)
        throw new Error(resourcesCopy.detail.noOwningTopic)
      return promoteResource(resource.topic_id, resource.id, to)
    },
    onSuccess: () => {
      addToast(resourcesCopy.detail.moved, 'success')
      refresh()
    },
    onError: (err) =>
      addToast(
        err instanceof Error ? err.message : resourcesCopy.detail.moveFailed,
        'error',
      ),
  })

  // The trail is complete while the page loads: its last crumb is the page's
  // name until the resource's own arrives.
  const crumbs: Crumb[] = [
    {
      label: workspace?.name ?? resourcesCopy.list.workspaceCrumb,
      to: ROUTES.home,
    },
    {
      label: resourcesCopy.list.crumb,
      to: workspace
        ? ROUTES.workspaceResources(workspace.workspace_id)
        : undefined,
    },
    {
      label: resource
        ? resource.name || resource.kind
        : resourcesCopy.detail.title,
    },
  ]

  if (isError) {
    return (
      <WsPageShell crumbs={crumbs} title={resourcesCopy.detail.title}>
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            {resourcesCopy.detail.loadFailed}
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {resourcesCopy.retry}
            </Button>
          </Inline>
        </Alert>
      </WsPageShell>
    )
  }

  if (isLoading || !resource) {
    return (
      <WsPageShell crumbs={crumbs} title={resourcesCopy.detail.title}>
        <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
      </WsPageShell>
    )
  }

  const flag = reviewFlag(resource)
  const view = getResourceView(resource.kind)
  // Any kind that receives events has a delivery trace; topic-scoped rows
  // (webhooks) resolve their workspace from the active one.
  const receivesEvents = kinds.some(
    (k) => k.kind === resource.kind && k.ingress,
  )
  const traceWorkspaceId = resource.workspace_id ?? activeWorkspaceId
  const viewResource = resource as unknown as ViewResource
  const handlers = view.eventHandlers?.(viewResource) ?? null
  const kind = kindDisplay(resource.kind, kinds)
  const editUrl = resourceEditUrl(resource)
  const scope = resourceScope(resource)
  const provenance = [
    resource.author?.display_name
      ? resource.author.kind === 'agent'
        ? resourcesCopy.detail.byAgent(resource.author.display_name)
        : resourcesCopy.detail.by(resource.author.display_name)
      : null,
    resourcesCopy.detail.created(formatRelative(resource.created_at, now)),
    resource.updated_at > resource.created_at
      ? resourcesCopy.detail.updated(formatRelative(resource.updated_at, now))
      : null,
    resource.promoted_from_scope
      ? resourcesCopy.detail.promotedFrom(
          resource.promoted_from_scope,
          resource.promoted_at
            ? formatRelative(resource.promoted_at, now)
            : undefined,
        )
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const visibility =
    scope === 'thread'
      ? resourcesCopy.detail.visibilityThread
      : scope === 'topic'
        ? resourcesCopy.detail.visibilityTopic
        : resourcesCopy.detail.visibilityWorkspace

  const moveButton = (to: 'topic' | 'workspace', label: string) => (
    <Button
      variant="outline"
      loading={promote.isPending && promote.variables === to}
      onClick={() => promote.mutate(to)}
    >
      {label}
    </Button>
  )

  return (
    <WsPageShell
      crumbs={crumbs}
      title={resource.name || resource.kind}
      meta={provenance}
      actions={
        <>
          {scope === 'thread' &&
            moveButton('topic', resourcesCopy.detail.moveToTopic)}
          {(scope === 'thread' || scope === 'topic') &&
            moveButton('workspace', resourcesCopy.detail.moveToWorkspace)}
          {editUrl && (
            <Button variant="outline" href={editUrl}>
              {resourcesCopy.detail.edit}
            </Button>
          )}
        </>
      }
    >
      <Stack gap="space-24">
        <Inline gap="space-8">
          <Chip variant="kind" icon={kind.icon} label={kind.label} />
          {flag && <StatusBadge tone="warning">{flag}</StatusBadge>}
          <Text variant="caption" tone="text-mute">
            {visibility}
          </Text>
        </Inline>
        <view.Body resource={resource} />

        {view.Panels && <view.Panels resource={viewResource} />}

        {receivesEvents && traceWorkspaceId && (
          <IngressEndpointCard
            workspaceId={traceWorkspaceId}
            resourceId={resource.id}
          />
        )}

        {receivesEvents && traceWorkspaceId && (
          <RecipeUsageCard
            workspaceId={traceWorkspaceId}
            resourceId={resource.id}
          />
        )}

        {handlers && traceWorkspaceId && (
          <WorkflowsCard
            workspaceId={traceWorkspaceId}
            topicId={handlers.topicId}
            suggestedEventType={handlers.eventType}
          />
        )}

        {receivesEvents && traceWorkspaceId && (
          <DeliveryTracePanel
            workspaceId={traceWorkspaceId}
            resourceId={resource.id}
          />
        )}
      </Stack>
    </WsPageShell>
  )
}

type Scope = 'thread' | 'topic' | 'workspace'

function resourceScope(r: TopicResource): Scope {
  if (r.thread_id) return 'thread'
  if (r.workspace_id) return 'workspace'
  return 'topic'
}
